/**
 * Thin wrapper around forward/reverse geocoding and haversine nearest-location
 * lookup. Used by map-picker and geocoding modes to resolve addresses or map
 * clicks to the nearest port/area from the locations dataset.
 *
 * The two directions use different providers on purpose:
 * - Forward (address search) goes through our own /api/v1/geocode proxy, where
 *   the server holds the Google Maps key, counts the daily Google allowance,
 *   and falls back to Nominatim once it runs out (see GeocodingService).
 * - Reverse (map click → address) stays on Nominatim directly from the browser,
 *   so it costs no Google quota and no server round-trip.
 *
 * Nominatim usage: https://nominatim.org/release-docs/develop/api/Overview/
 */

const GEOCODE_URL = '/api/v1/geocode';
const RESOLVE_MAP_LINK_URL = '/api/v1/resolve_map_link';
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
const DEFAULT_TIMEOUT_MS = 10000;
// Link resolution follows redirects (and sometimes geocodes) server-side, so
// give it a longer budget than a plain geocode.
const RESOLVE_TIMEOUT_MS = 12000;

// Google/Apple Maps share links the resolver endpoint understands. Kept in sync
// with MapLinkResolverService::ALLOWED_HOST_SUFFIXES on the server.
const MAP_LINK_HOST = /(^|\.)(google\.com|goo\.gl|apple\.com)$|^maps\.apple$/i;

/**
 * Whether a pasted string is a Google/Apple Maps link (vs. a typed address),
 * so the caller can route it to the link resolver instead of forward geocoding.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isMapLink(text) {
  const trimmed = (text || '').trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    return MAP_LINK_HOST.test(new URL(trimmed).hostname);
  } catch (err) {
    return false;
  }
}

/**
 * Resolve a pasted Google/Apple Maps link to coordinates via the server proxy.
 * Only coordinates are returned; the caller reverse-geocodes them for the
 * displayed address, so a link and a map click yield the same kind of label.
 *
 * @param {string} url - The pasted share link
 * @param {number} [timeoutMs]
 * @returns {Promise<{ lat: number, lng: number, provider: string } | null>}
 */
export function resolveMapLink(url, timeoutMs = RESOLVE_TIMEOUT_MS) {
  const trimmed = (url || '').trim();
  if (!trimmed) return Promise.resolve(null);

  const params = new URLSearchParams({ url: trimmed });
  return fetchJson(`${RESOLVE_MAP_LINK_URL}?${params.toString()}`, timeoutMs, {
    credentials: 'same-origin',
  })
    .then((payload) => {
      const result = payload && payload.result;
      if (!result) return null;
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng, provider: result.provider };
    })
    .catch(() => null);
}

/**
 * Haversine distance between two points in km (spherical earth).
 *
 * @param {number} lat1 - Latitude of first point (degrees)
 * @param {number} lng1 - Longitude of first point (degrees)
 * @param {number} lat2 - Latitude of second point (degrees)
 * @param {number} lng2 - Longitude of second point (degrees)
 * @returns {number} Distance in kilometres
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth radius in km
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Find the location from the dataset that is nearest to the given coordinates.
 * Locations without valid lat/lng are skipped.
 *
 * @param {number} lat - Latitude (degrees)
 * @param {number} lng - Longitude (degrees)
 * @param {Array<{ lat?: number, lng?: number, [key: string]: unknown }>} locations - Dataset from selectors (or any list with optional lat/lng)
 * @returns {{ location: object, distanceKm: number } | null} Nearest location and distance, or null if none have coordinates
 */
export function findNearestLocation(lat, lng, locations) {
  if (!locations || !Array.isArray(locations) || locations.length === 0) {
    return null;
  }

  const withCoords = locations.filter(
    (loc) =>
      typeof loc.lat === 'number' &&
      typeof loc.lng === 'number' &&
      Number.isFinite(loc.lat) &&
      Number.isFinite(loc.lng)
  );

  if (withCoords.length === 0) return null;

  let nearest = withCoords[0];
  let minDist = haversineDistance(lat, lng, nearest.lat, nearest.lng);

  for (let i = 1; i < withCoords.length; i++) {
    const loc = withCoords[i];
    const d = haversineDistance(lat, lng, loc.lat, loc.lng);
    if (d < minDist) {
      minDist = d;
      nearest = loc;
    }
  }

  return { location: nearest, distanceKm: minDist };
}

/**
 * Fetch JSON with an optional timeout.
 *
 * @param {string} url - Full URL (with query params)
 * @param {number} [timeoutMs] - Request timeout in ms
 * @param {object} [options] - Extra fetch options merged over the defaults
 * @returns {Promise<object|object[]>} Parsed JSON
 */
function fetchJson(url, timeoutMs = DEFAULT_TIMEOUT_MS, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal: controller.signal,
    ...options,
  })
    .then((res) => {
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`Geocoding error: ${res.status}`);
      return res.json();
    })
    .catch((err) => {
      clearTimeout(timeoutId);
      throw err;
    });
}

// The proxy answers { result: { lat, lng, display_name, provider } | null }.
function unwrapResult(payload) {
  const result = payload && payload.result;
  if (!result) return null;

  const lat = parseFloat(result.lat);
  const lng = parseFloat(result.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    lat,
    lng,
    display_name: result.display_name || `${lat}, ${lng}`,
    provider: result.provider,
  };
}

// Strip "Port of <X>" → "<X>" and "<X> Port" → "<X>" so Nominatim finds the
// city rather than failing on the word "port". Safe for proper names like
// "Port Said" which don't match either pattern.
function normalizePortQuery(query) {
  return query
    .replace(/^port\s+of\s+/i, '')
    .replace(/\s+port$/i, '')
    .trim();
}

// Returns an ordered list of query strings to try. Starts with the original,
// then progressively shorter comma-suffix slices, then normalized variants.
// Fallback candidates must keep ≥ 2 comma parts to avoid resolving a bare
// country name to its geographic centroid instead of the intended city.
//
// "23 Rue X, Alexandria, Egypt" → ["23 Rue X, Alexandria, Egypt", "Alexandria, Egypt"]
// "Alexandria, Egypt"           → ["Alexandria, Egypt"]  (no unsafe fallback)
// "Port of Rotterdam, Netherlands" → [..., "Rotterdam, Netherlands"]
function buildQueryCandidates(query) {
  const candidates = [query];

  const parts = query.split(',').map((p) => p.trim()).filter(Boolean);
  for (let i = 1; i < parts.length; i++) {
    if (parts.length - i >= 2) {
      candidates.push(parts.slice(i).join(', '));
    }
  }

  const normalized = normalizePortQuery(query);
  if (normalized !== query) {
    candidates.push(normalized);
    const normParts = normalized.split(',').map((p) => p.trim()).filter(Boolean);
    for (let i = 1; i < normParts.length; i++) {
      if (normParts.length - i >= 2) {
        candidates.push(normParts.slice(i).join(', '));
      }
    }
  }

  return [...new Set(candidates)];
}

function fetchGeocodeOnce(query, timeoutMs) {
  const params = new URLSearchParams({ q: query });
  return fetchJson(`${GEOCODE_URL}?${params.toString()}`, timeoutMs, {
    credentials: 'same-origin',
  })
    .then(unwrapResult)
    .catch(() => null);
}

async function tryQueryCandidates(candidates, timeoutMs) {
  for (const candidate of candidates) {
    const result = await fetchGeocodeOnce(candidate, timeoutMs);
    if (result) return result;
  }
  return null;
}

/**
 * Forward geocode: address or place name → coordinates and display name.
 * Tries progressively simplified versions of the query before giving up,
 * so partial or verbose addresses still resolve without falling back to
 * dangerously generic single-term queries.
 *
 * @param {string} query - Address or place name to search
 * @param {number} [timeoutMs] - Request timeout in ms
 * @returns {Promise<{ lat: number, lng: number, display_name: string } | null>} First result or null if none
 */
export function forwardGeocode(query, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const trimmed = (query || '').trim();
  if (!trimmed) return Promise.resolve(null);
  return tryQueryCandidates(buildQueryCandidates(trimmed), timeoutMs);
}

/**
 * Reverse geocode: coordinates → address / display name.
 * Hits Nominatim directly from the browser — no Google quota, no round-trip.
 *
 * @param {number} lat - Latitude (degrees)
 * @param {number} lng - Longitude (degrees)
 * @param {number} [timeoutMs] - Request timeout in ms
 * @returns {Promise<{ lat: number, lng: number, display_name: string } | null>} Result or null on failure
 */
export function reverseGeocode(lat, lng, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
    return Promise.resolve(null);
  }

  const params = new URLSearchParams({
    lat: String(latNum),
    lon: String(lngNum),
    format: 'json',
  });

  return fetchJson(`${NOMINATIM_BASE}/reverse?${params.toString()}`, timeoutMs).then((data) => {
    const resultLat = parseFloat(data.lat);
    const resultLng = parseFloat(data.lon);
    if (!Number.isFinite(resultLat) || !Number.isFinite(resultLng)) return null;
    return {
      lat: resultLat,
      lng: resultLng,
      display_name: data.display_name || `${resultLat}, ${resultLng}`,
    };
  });
}
